import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { reduceLayerTwoData } from 'helpers/data';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import RenderingTile from 'components/molecules/RenderingTile';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import SocialAdSetOverviewPivotTable from 'components/molecules/tables/SocialAdSetOverviewPivotTable';
import { convertToDisplayValue } from 'helpers/strings';
import SocialConversionsChart from 'components/molecules/charts/SocialConversionsChart';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';

class ReachExpandModule extends Component {
    render() {
        const {
            objective,
            xAxisLabel,
            backfillBuckets,
            layer = LAYERTHREE,
            layerTwo,
            showConversionRevenue,
            entityMeta,
            entityObjective,
            entityObjectiveLabelCode
        } = this.props;
        const {
            conversionTermSingular,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);
        const stub = `l${layer}-social`;
        const { timeData, adsets, campaigns, uniqueMeasures: { adset: uniqueAdset }, actions } = this.props.module;
        const uniqueAdsetBucket = convertToDisplayValue(get(uniqueAdset, 'params.timeBucket', 'daily'));
        const hasTimeData = get(timeData, 'hasData', null);
        const hasAdSetData = get(adsets, 'hasData', null);
        const hasUniqueAdSetData = uniqueAdset.hasData;
        const reportingObjective = objective.toLowerCase().replace('_', '-');
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', null);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = get(this.props, 'entityId', null);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }
        const rankedReach = hasUniqueAdSetData ? uniqueAdset.rankings.reach_mean.reduce((prev, adset) => {
            const adSetName = uniqueAdset.adSetIdMap[adset.name].name;
            const statistics = uniqueAdset[adset.name].statistics;
            prev.data.push(statistics);
            prev.categories.push(adSetName);

            return prev;
        }, { categories: [], data: [] }) : {};

        const objectives = get(entityMeta, 'social.objectives', []);
        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && (layer === LAYERTWO) && (objectives.length > 1));
        const showCampaignSummaryTable = !!(hasAdSetData && layer === LAYERTHREE);

        const layerTwoSummaryAccessors = ['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversion_rate', 'engagements', 'engagement_rate', 'spend'];

        return (
            <React.Fragment>

                {/* Reach vs. Unique Clicks */}
                {hasUniqueAdSetData ? (
                    <TwoAxisBarWithScatterChart
                        title={'MEAN REACH VS. MEAN UNIQUE CLICKS'}
                        subTitle={`(${uniqueAdsetBucket})`}
                        exportName={`${stub}-${reportingObjective}-mean-reach-vs-mean-unique-clicks`}
                        data={rankedReach.data}
                        yBar="reach_mean"
                        yBarTitle="Mean Reach"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yBarUserOptions={{ formatByType: 'floatX2' }}
                        scatter="unique_clicks_mean"
                        scatterTitle="Mean Unique Clicks"
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('number')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'number' }}
                        animateDuration={2000}
                        categories={rankedReach.categories} />
                ) : (
                    <RenderingTile height='400px' show={hasUniqueAdSetData} />
                )}

                {/* Impressions VS. CTR */}
                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title="Impressions VS. CTR"
                        exportName={`${stub}-${reportingObjective}-impressions-vs-ctr`}
                        data={timeData.data}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine="ctr"
                        yLineTitle="CTR"
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                {/* Reach vs. Frequency */}
                {hasUniqueAdSetData ? (
                    <TwoAxisBarWithScatterChart
                        title={'Mean Reach vs. Mean Frequency'}
                        subTitle={`(${uniqueAdsetBucket})`}
                        exportName={`${stub}-${reportingObjective}-mean-reach-vs-mean-frequency`}
                        data={rankedReach.data}
                        yBar="reach_mean"
                        yBarTitle="Mean Reach"
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yBarUserOptions={{ formatByType: 'floatX2' }}
                        scatter="frequency_mean"
                        scatterTitle="Mean Frequency"
                        yScatterOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('floatX2')
                            }
                        }}
                        yScatterUserOptions={{ formatByType: 'floatX2' }}
                        animateDuration={2000}
                        categories={rankedReach.categories} />
                ) : (
                    <RenderingTile height='400px' show={hasUniqueAdSetData} />
                )}

                <SocialConversionsChart actions={actions} exportNamePrefix={`${stub}-${reportingObjective}`} />

                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        showConversionRevenue={showConversionRevenue}
                        exportName={`l2-social-${reportingObjective}-table-performance`}
                        accessors={layerTwoSummaryAccessors}
                        cacheKey={`l2-social-${reportingObjective}-table-performance-${userId}-${entityId}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )}

                {showCampaignSummaryTable ? (
                    <SocialAdSetOverviewPivotTable
                        title="Campaign Overview"
                        exportName={`l3-social-${reportingObjective}-summary-adsets-overview`}
                        adSets={adsets}
                        showConversionRevenue={showConversionRevenue}
                    />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable} />
                )}

                <SocialActionOverviewTable
                    exportName={`${stub}-${reportingObjective}-action-overview`}
                    actions={actions}
                    campaigns={campaigns}
                    conversionTermSingular={conversionTermSingular}
                    cacheKye={`${stub}-${reportingObjective}-action-overview-${userId}-${entityId}`}
                />
            </React.Fragment>
        );
    }
}

ReachExpandModule.propTypes = {
    module: PropTypes.shape({
        actions: PropTypes.object,
        ads: PropTypes.object,
        adsets: PropTypes.object,
        callouts: PropTypes.object,
        campaigns: PropTypes.object,
        timeData: PropTypes.object
    }).isRequired,
    objective: PropTypes.string.isRequired
};

export default ReachExpandModule;



// WEBPACK FOOTER //
// ./src/components/organisms/social/ReachExpandModule.js